/* eslint-disable no-continue, func-names */
import * as Yup from 'yup'

export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 128

export const STRONG_PASSWORD_COUNTS = {
  minUppercase: 2,
  minLowercase: 2,
  minDigits: 2,
  minSpecial: 2
}

const UPPER = /[A-Z]/
const LOWER = /[a-z]/
const DIGIT = /\d/
const SPECIAL = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/
const WHITESPACE = /\s/

const PERSONAL_INFO_RUN_LENGTH = 4

function countMatches(value: string, charClass: RegExp): number {
  let count = 0
  for (const char of value) {
    if (charClass.test(char)) count += 1
  }
  return count
}

const isLetter = (char: string): boolean => char >= 'a' && char <= 'z'
const isDigit = (char: string): boolean => char >= '0' && char <= '9'

export function hasSequentialCharacters(value: string): boolean {
  const lower = value.toLowerCase()
  for (let i = 1; i < lower.length; i += 1) {
    const prev = lower[i - 1]
    const curr = lower[i]
    const sameClass =
      (isLetter(prev) && isLetter(curr)) || (isDigit(prev) && isDigit(curr))
    if (!sameClass) continue
    const delta = (curr.codePointAt(0) ?? 0) - (prev.codePointAt(0) ?? 0)
    if (delta === 1 || delta === -1) return true
  }
  return false
}

const COMMON_PASSWORDS = new Set<string>([
  'password',
  'password1',
  'password123',
  'password@123',
  'qwerty',
  'qwerty123',
  'admin',
  'admin123',
  'welcome',
  'welcome1',
  'welcome@123',
  '123456',
  '12345678',
  '123456789',
  'letmein',
  'iloveyou',
  'monkey',
  'abc123',
  'passw0rd',
  'trustno1'
])

export interface PasswordSchemaOptions {
  personalInfoFields?: string[]
  extraPersonalInfo?: (string | undefined | null)[]
  requiredMessage?: string
  minUppercase?: number
  minLowercase?: number
  minDigits?: number
  minSpecial?: number
  disallowSequentialCharacters?: boolean
  blockPersonalInfoSubstrings?: boolean
}

function classMessage(
  count: number,
  noun: string,
  plural = `${noun}s`
): string {
  return `Must contain at least ${count} ${count === 1 ? noun : plural}`
}
function personalInfoTokens(raw: string, dropEmailDomain: boolean): string[] {
  const at = raw.indexOf('@')
  const identity = dropEmailDomain && at > 0 ? raw.substring(0, at) : raw
  const separators = dropEmailDomain ? /[\s._\-+]+/ : /[\s._\-@+]+/
  return identity
    .toLowerCase()
    .split(separators)
    .filter((token) => token.length >= PERSONAL_INFO_RUN_LENGTH)
}

export interface PersonalInfoMatchOptions {
  matchSubstrings?: boolean
}
function containsTokenRun(lower: string, token: string): boolean {
  for (let i = 0; i + PERSONAL_INFO_RUN_LENGTH <= token.length; i += 1) {
    if (lower.includes(token.substring(i, i + PERSONAL_INFO_RUN_LENGTH))) {
      return true
    }
  }
  return false
}

function containsEmailLocalPart(lower: string, raw: string): boolean {
  const at = raw.indexOf('@')
  return at > 3 && lower.includes(raw.substring(0, at).toLowerCase())
}

function matchesPersonalInfo(
  lower: string,
  raw: string,
  matchSubstrings: boolean
): boolean {
  const tokens = personalInfoTokens(raw, matchSubstrings)

  if (matchSubstrings) {
    return tokens.some((token) => containsTokenRun(lower, token))
  }
  return (
    tokens.some((token) => lower.includes(token)) ||
    containsEmailLocalPart(lower, raw)
  )
}

export function containsPersonalInfo(
  password: string,
  personalInfo: (string | undefined | null)[],
  options: PersonalInfoMatchOptions = {}
): boolean {
  if (!password) return false
  const { matchSubstrings = false } = options
  const lower = password.toLowerCase()

  return personalInfo.some(
    (raw) => !!raw && matchesPersonalInfo(lower, raw, matchSubstrings)
  )
}

export function createPasswordSchema(
  options: PasswordSchemaOptions = {}
): Yup.StringSchema {
  const {
    requiredMessage = 'Password is required',
    personalInfoFields = [],
    extraPersonalInfo = [],
    minUppercase = 1,
    minLowercase = 1,
    minDigits = 1,
    minSpecial = 1,
    disallowSequentialCharacters = false,
    blockPersonalInfoSubstrings = false
  } = options

  let schema = Yup.string()
    .required(requiredMessage)
    .min(
      PASSWORD_MIN_LENGTH,
      `Password must be at least ${PASSWORD_MIN_LENGTH} characters`
    )
    .max(
      PASSWORD_MAX_LENGTH,
      `Password must be at most ${PASSWORD_MAX_LENGTH} characters`
    )
    .test(
      'min-uppercase',
      classMessage(minUppercase, 'uppercase letter'),
      (value) => !!value && countMatches(value, UPPER) >= minUppercase
    )
    .test(
      'min-lowercase',
      classMessage(minLowercase, 'lowercase letter'),
      (value) => !!value && countMatches(value, LOWER) >= minLowercase
    )
    .test(
      'min-digits',
      classMessage(minDigits, 'number'),
      (value) => !!value && countMatches(value, DIGIT) >= minDigits
    )
    .test(
      'min-special',
      classMessage(minSpecial, 'special character'),
      (value) => !!value && countMatches(value, SPECIAL) >= minSpecial
    )
    .test(
      'no-whitespace',
      'Password must not contain spaces',
      (value) => !!value && !WHITESPACE.test(value)
    )
    .test(
      'not-common',
      'This password is too common — choose a stronger one',
      (value) => !!value && !COMMON_PASSWORDS.has(value.toLowerCase())
    )
    .test(
      'no-personal-info',
      'Password must not contain your name, email, or phone number',
      function (value) {
        if (!value) return true
        const siblings = personalInfoFields.map(
          (f) =>
            (this.parent as Record<string, unknown>)[f] as string | undefined
        )
        return !containsPersonalInfo(
          value,
          [...siblings, ...extraPersonalInfo],
          { matchSubstrings: blockPersonalInfoSubstrings }
        )
      }
    )

  if (disallowSequentialCharacters) {
    schema = schema.test(
      'no-sequential-characters',
      'Password must not contain sequential characters (e.g. "ab", "12")',
      (value) => !!value && !hasSequentialCharacters(value)
    )
  }

  return schema
}

/* ────────────────────────────── generator ────────────────────────────── */

const UPPER_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWER_CHARS = 'abcdefghijklmnopqrstuvwxyz'
const DIGIT_CHARS = '0123456789'
/** Kept to a subset of SPECIAL that needs no escaping when copied around. */
const SPECIAL_CHARS = '!@#$%^&*()_+-=?.,;:<>'
const ALL_CHARS = UPPER_CHARS + LOWER_CHARS + DIGIT_CHARS + SPECIAL_CHARS

const DEFAULT_GENERATED_LENGTH = 16
const MAX_GENERATE_ATTEMPTS = 100

function randomInt(max: number): number {
  if (typeof window.crypto?.getRandomValues !== 'function') {
    throw new Error('Secure random source unavailable')
  }
  const buffer = new Uint32Array(1)
  const limit = Math.floor(0xffffffff / max) * max
  let value = limit
  while (value >= limit) {
    window.crypto.getRandomValues(buffer)
    ;[value] = buffer
  }
  return value % max
}

function pickNonSequential(charset: string, prev: string | undefined): string {
  for (;;) {
    const char = charset.charAt(randomInt(charset.length))
    if (!prev || !hasSequentialCharacters(prev + char)) return char
  }
}

function shuffle<T>(items: T[]): T[] {
  const result = [...items]
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = randomInt(i + 1)
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

export interface GeneratePasswordOptions {
  length?: number
  minUppercase?: number
  minLowercase?: number
  minDigits?: number
  minSpecial?: number
  personalInfo?: (string | undefined | null)[]
}

export function generatePassword(
  options: GeneratePasswordOptions = {}
): string {
  const {
    length = DEFAULT_GENERATED_LENGTH,
    minUppercase = 1,
    minLowercase = 1,
    minDigits = 1,
    minSpecial = 1,
    personalInfo = []
  } = options

  const quotaFor = (count: number, charset: string): string[] =>
    new Array<string>(Math.max(count, 0)).fill(charset)

  const quotas: string[] = [
    ...quotaFor(minUppercase, UPPER_CHARS),
    ...quotaFor(minLowercase, LOWER_CHARS),
    ...quotaFor(minDigits, DIGIT_CHARS),
    ...quotaFor(minSpecial, SPECIAL_CHARS)
  ]
  const targetLength = Math.max(length, quotas.length, PASSWORD_MIN_LENGTH)
  while (quotas.length < targetLength) quotas.push(ALL_CHARS)

  let candidate = ''
  for (let attempt = 0; attempt < MAX_GENERATE_ATTEMPTS; attempt += 1) {
    const chars: string[] = []
    for (const charset of shuffle(quotas)) {
      chars.push(pickNonSequential(charset, chars.at(-1)))
    }
    candidate = chars.join('')

    const clean =
      !COMMON_PASSWORDS.has(candidate.toLowerCase()) &&
      !containsPersonalInfo(candidate, personalInfo, { matchSubstrings: true })
    if (clean) return candidate
  }

  return candidate
}
